import React, { useEffect, useState, useCallback } from 'react';
import api, { fmtINR, fmtNum, fmtDate } from '@/lib/api';
import { Loading, SectionTitle, KpiCard } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { AddressDialog } from '@/components/AddressDialog';
import { ChangePasswordCard } from '@/components/ChangePassword';
import { MapPin, Plus, Trash2, Pencil, Building2, TrendingUp, Wallet, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerProfile() {
  const [profile, setProfile] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState({ category: 'Delivery issue', subject: '', message: '' });
  const [openAddr, setOpenAddr] = useState(false);
  const [editAddr, setEditAddr] = useState(null);
  const [openTicket, setOpenTicket] = useState(false);
  const [det, setDet] = useState(null);
  const [savingDet, setSavingDet] = useState(false);

  const load = useCallback(() => {
    api.get('/customers/me').then((r) => {
      setProfile(r.data);
      setDet({
        company_name: r.data.company_name || '', contact_person: r.data.contact_person || '',
        phone: r.data.phone || '', gstin: r.data.gstin || '', pan: r.data.pan || '',
        industry_type: r.data.industry_type || '', billing_address: r.data.billing_address || '',
      });
    });
    api.get('/tickets/mine').then((r) => setTickets(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!profile) return <Loading />;
  const a = profile.analytics || {};

  const saveDetails = async () => {
    setSavingDet(true);
    try { await api.put('/customers/me', det); toast.success('Company details updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingDet(false); }
  };

  const saveAddress = async (v) => {
    const payload = { label: v.label, address: v.address, lat: Number(v.lat) || 0, lng: Number(v.lng) || 0, is_default: v.is_default };
    try {
      if (editAddr) await api.put(`/customers/me/addresses/${editAddr.id}`, payload);
      else await api.post('/customers/me/addresses', { ...payload, is_default: v.is_default || profile.addresses.length === 0 });
      toast.success(editAddr ? 'Location updated' : 'Location added');
      load();
    } catch (e) { toast.error('Failed'); throw e; }
  };
  const delAddress = async (id) => { await api.delete(`/customers/me/addresses/${id}`); load(); };
  const createTicket = async () => {
    try { await api.post('/tickets', ticket); toast.success('Ticket raised'); setOpenTicket(false); setTicket({ category: 'Delivery issue', subject: '', message: '' }); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center"><Building2 className="h-6 w-6 text-primary-foreground" /></div>
        <div><h2 className="font-display text-lg font-semibold">{profile.company_name}</h2><p className="text-sm text-muted-foreground">{profile.customer_type} · {profile.gstin || 'No GSTIN'}</p></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Total Litres" value={`${fmtNum(a.total_litres || 0)}`} icon={Package} />
        <KpiCard label="Total Spend" value={fmtINR(a.total_revenue || 0)} icon={TrendingUp} />
        <KpiCard label="Outstanding" value={fmtINR(a.outstanding || 0)} icon={Wallet} />
        <KpiCard label="Total Orders" value={fmtNum(a.total_orders || 0)} icon={Package} />
      </div>

      <Tabs defaultValue="addresses">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="addresses">Locations</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="support">Support</TabsTrigger>
        </TabsList>

        <TabsContent value="addresses" className="space-y-2 mt-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => { setEditAddr(null); setOpenAddr(true); }} data-testid="add-address-button"><Plus className="h-4 w-4 mr-1" />Add Location</Button>
          </div>
          {profile.addresses.map((ad) => (
            <div key={ad.id} className="rounded-xl border border-border bg-card p-3 flex items-start justify-between">
              <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-1 text-primary" /><div><p className="font-medium text-sm">{ad.label} {ad.is_default && <span className="text-xs text-primary">(Default)</span>}</p><p className="text-xs text-muted-foreground">{ad.address}</p></div></div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => { setEditAddr(ad); setOpenAddr(true); }} data-testid={`edit-address-${ad.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => delAddress(ad.id)} data-testid={`delete-address-${ad.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          {profile.addresses.length === 0 && <p className="text-sm text-muted-foreground">No delivery locations yet. Add one to start ordering.</p>}
          <AddressDialog open={openAddr} onOpenChange={setOpenAddr} initial={editAddr} onSave={saveAddress} title={editAddr ? 'Edit Delivery Location' : 'Add Delivery Location'} />
        </TabsContent>

        <TabsContent value="details" className="mt-3">
          <div className="rounded-xl border border-border bg-card p-4 space-y-3 text-sm" data-testid="customer-details-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Company Name</Label><Input value={det?.company_name || ''} onChange={(e) => setDet({ ...det, company_name: e.target.value })} data-testid="det-company-input" /></div>
              <div><Label className="text-xs">Contact Person</Label><Input value={det?.contact_person || ''} onChange={(e) => setDet({ ...det, contact_person: e.target.value })} data-testid="det-contact-input" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={det?.phone || ''} onChange={(e) => setDet({ ...det, phone: e.target.value })} data-testid="det-phone-input" /></div>
              <div><Label className="text-xs">Email (read-only)</Label><Input value={profile.email} disabled /></div>
              <div><Label className="text-xs">GSTIN</Label><Input value={det?.gstin || ''} onChange={(e) => setDet({ ...det, gstin: e.target.value })} data-testid="det-gstin-input" /></div>
              <div><Label className="text-xs">PAN</Label><Input value={det?.pan || ''} onChange={(e) => setDet({ ...det, pan: e.target.value })} data-testid="det-pan-input" /></div>
              <div><Label className="text-xs">Industry Type</Label><Input value={det?.industry_type || ''} onChange={(e) => setDet({ ...det, industry_type: e.target.value })} data-testid="det-industry-input" /></div>
            </div>
            <div><Label className="text-xs">Billing Address</Label><Textarea rows={2} value={det?.billing_address || ''} onChange={(e) => setDet({ ...det, billing_address: e.target.value })} data-testid="det-billing-input" /></div>
            <div className="border-t border-border pt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-muted-foreground">
              <span>Credit Limit: <b className="text-foreground">{fmtINR(profile.credit_limit)}</b></span>
              <span>Credit Used: <b className="text-foreground">{fmtINR(profile.credit_used)}</b></span>
              <span>Payment Terms: <b className="text-foreground">{profile.payment_terms}</b></span>
            </div>
            <div className="flex justify-end"><Button onClick={saveDetails} disabled={savingDet} data-testid="save-details-button">{savingDet ? 'Saving...' : 'Save Changes'}</Button></div>
          </div>
        </TabsContent>

        <TabsContent value="support" className="space-y-2 mt-3">
          <div className="flex justify-end">
            <Dialog open={openTicket} onOpenChange={setOpenTicket}>
              <DialogTrigger asChild><Button size="sm" data-testid="new-ticket-button"><Plus className="h-4 w-4 mr-1" />New Ticket</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Raise Support Ticket</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Category</Label>
                    <Select value={ticket.category} onValueChange={(v) => setTicket({ ...ticket, category: v })}><SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{['Delivery issue', 'Wrong quantity', 'Invoice issue', 'Payment issue', 'Product issue', 'Driver issue', 'Other'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label>Subject</Label><Input value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} data-testid="ticket-subject-input" /></div>
                  <div><Label>Message</Label><Textarea value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} data-testid="ticket-message-input" /></div>
                </div>
                <DialogFooter><Button onClick={createTicket} data-testid="submit-ticket-button">Submit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {tickets.length === 0 && <p className="text-sm text-muted-foreground">No tickets raised.</p>}
          {tickets.map((t) => (
            <div key={t.id} className="rounded-xl border border-border bg-card p-3">
              <div className="flex justify-between"><p className="font-medium text-sm">{t.subject}</p><span className="text-xs rounded-full bg-muted px-2 py-0.5">{t.status}</span></div>
              <p className="text-xs text-muted-foreground mt-1">{t.category} · {fmtDate(t.created_at)}</p>
              {(t.replies || []).map((r, i) => <p key={`${r.at || r.by}-${i}`} className="text-xs mt-1 border-l-2 border-primary pl-2">{r.by}: {r.text}</p>)}
            </div>
          ))}
        </TabsContent>
      </Tabs>
      <div className="max-w-md"><ChangePasswordCard /></div>
    </div>
  );
}

const Row = ({ l, r }) => <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-medium">{r}</span></div>;
